import React, { useRef, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import { lazy } from 'react-loosely-lazy';
import Drawer from '@atlaskit/drawer';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { gridSize } from '@atlassian/jira-common-styles/src';
import { ReportErrors } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { IntlProvider, useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder';
import { monitor } from '@atlassian/jira-portfolio-3-common/src/analytics/performance';
import {
	PACKAGE_NAME,
	ERROR_REPORTING_TEAM,
	PERFORMANCE_CREATE_PLAN_WIZARD_LOAD_TIME,
} from '../common/constants';
import { useCreateNewPlanWizardDrawer, useImportedMessagesForLocale } from '../controllers';
import type { Props as PlanWizardProps } from './content/types';
import messages from './messages';

const CrossIconMedium = () => {
	const { formatMessage } = useIntl();
	return <CrossIcon size="medium" label={formatMessage(messages.close)} />;
};

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const AsyncPlanWizard = lazy<ComponentType<PlanWizardProps>>(
	() => {
		monitor.startTask(PERFORMANCE_CREATE_PLAN_WIZARD_LOAD_TIME);
		return import(
			/* webpackChunkName: "async-portfolio-3-create-new-plan-wizard" */ './old-content'
		).then((module) => module.default);
	},
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const AsyncPlanWizardNew = lazy<ComponentType<PlanWizardProps>>(
	() => {
		monitor.startTask(PERFORMANCE_CREATE_PLAN_WIZARD_LOAD_TIME);
		return import(
			/* webpackChunkName: "async-portfolio-3-create-new-plan-wizard-new" */ './content'
		).then((module) => module.default);
	},
	{ ssr: false },
);

const CreateNewPlanWizardDrawer = () => {
	const [{ isOpen, plan }, { closeDrawer }] = useCreateNewPlanWizardDrawer();
	const { loading, intlMessages, locale } = useImportedMessagesForLocale();
	const keepMounted = useRef(false);

	if (!keepMounted.current && !isOpen) return null;
	// Keep mounted to Drawer close animation works
	if (!keepMounted.current) keepMounted.current = true;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleRequestClose = (e: any) => {
		if (e.key === 'Escape') {
			return;
		}
		closeDrawer();
	};

	const spinner = (
		<Center>
			<Spinner size="xlarge" />
		</Center>
	);

	// Wrapping the AsyncPlanWizard component directly in Placeholder results the Drawer
	// flickering when it is opened immediately after the /plans page loads - either through
	// the create-plan=true query param, or by quickly clicking the top RHS Create plan
	// button. Wrapping the parent Drawer in Placeholder resolves this issue.
	const content = (() => {
		if (loading) return spinner;
		if (!intlMessages) return <AsyncPlanWizard plan={plan} />;

		return (
			<IntlProvider messages={intlMessages} locale={locale}>
				{ff('sploverview-create-experience') ? (
					<AsyncPlanWizardNew plan={plan} />
				) : (
					<AsyncPlanWizard plan={plan} />
				)}
			</IntlProvider>
		);
	})();

	return (
		<Placeholder name="create-new-plan-wizard" fallback={null}>
			<Drawer isOpen={isOpen} width="full" onClose={handleRequestClose} icon={CrossIconMedium}>
				<Box xcss={containerStyles}>
					<ReportErrors
						id={PACKAGE_NAME}
						packageName={PACKAGE_NAME}
						teamName={ERROR_REPORTING_TEAM}
						sendToPrivacyUnsafeSplunk
					>
						{content}
					</ReportErrors>
				</Box>
			</Drawer>
		</Placeholder>
	);
};

export default CreateNewPlanWizardDrawer;

const containerStyles = xcss({
	marginTop: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Center = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `calc(100vh - ${gridSize * 3}px)`,
	maxHeight: '540px',
});
